import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { Info, Plus, Pencil, X, ChevronDown } from "lucide-react";
import { DragIco } from "../../../../components/icons";

const getStatusClassName = (status) =>
  String(status).toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");

function CareerInfoTooltip({ isTouchDevice }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);

    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick);
    };
  }, [isOpen]);

  return (
    <span
      ref={tooltipRef}
      className="term-list__label-info"
      role="button"
      tabIndex={0}
      aria-expanded={isOpen || isHovering}
      aria-label="This career step is no longer in your Careers I'm Exploring list."
      onClick={(e) => {
        e.stopPropagation();
        if (isTouchDevice) {
          setIsOpen((prev) => !prev);
        }
      }}
      onPointerEnter={() => {
        if (!isTouchDevice) {
          setIsHovering(true);
        }
      }}
      onPointerLeave={() => {
        if (!isTouchDevice) {
          setIsHovering(false);
        }
      }}
      onFocus={() => {
        if (!isTouchDevice) {
          setIsHovering(true);
        }
      }}
      onBlur={() => {
        if (!isTouchDevice) {
          setIsHovering(false);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (isTouchDevice) {
            setIsOpen((prev) => !prev);
          } else {
            setIsHovering((prev) => !prev);
          }
        }
      }}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <Info size={14} strokeWidth={2.4} />
      {(isTouchDevice ? isOpen : isHovering) && (
        <span className="term-list__label-info-tooltip" role="tooltip">
          This career step is no longer in your Careers I&apos;m Exploring list.
        </span>
      )}
    </span>
  );
}

function TaskRow({
  task,
  termId,
  index,
  isDragging,
  isDropTarget,
  isTouchDragging,
  onEditTask,
  onOpenDeleteModal,
  onUpdateTaskStatus,
  onDesktopDragStart,
  onDesktopDragOver,
  onDesktopDrop,
  onDesktopDragEnd,
  onTouchStart,
  exploredCareerNames = [],
}) {
  const isTouchDevice =
    typeof window !== "undefined" &&
    (navigator.maxTouchPoints > 0 ||
      window.matchMedia?.("(pointer: coarse)")?.matches);
  const taskCareerIsMissing =
    task.career !== "Any Career" &&
    (!task.career ||
    !exploredCareerNames.some(
      (career) =>
        String(career).trim().toLowerCase() ===
        String(task.career).trim().toLowerCase(),
    ));

  return (
    <div
      data-action-plan-task-row="true"
      data-action-plan-term-id={termId}
      data-action-plan-task-id={task.id}
      className={classNames("term-list", {
        "is-dragging": isDragging || isTouchDragging,
        "is-drop-target": isDropTarget,
      })}
      draggable={!isTouchDevice}
      onDragStart={(event) => onDesktopDragStart(event, termId, task.id)}
      onDragOver={(event) => onDesktopDragOver(event, termId, task.id)}
      onDrop={(event) => onDesktopDrop(event, termId, task.id)}
      onDragEnd={onDesktopDragEnd}
      onTouchStart={(event) => onTouchStart(event, termId, task.id)}
      onTouchMove={(event) => event.preventDefault()}
    >
      <div className="d-flex justify-between align-items-center mobile-show">
        <label className="term-list__label">
          <span>{task.career}</span>
          {taskCareerIsMissing && <CareerInfoTooltip isTouchDevice={isTouchDevice} />}
        </label>
        <button
          type="button"
          className="term-list__delete term-list__delete__mobile"
          aria-label={`Delete ${task.title}`}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={() => onOpenDeleteModal(termId, task)}
        >
          <X size={16} strokeWidth={1.8} />
        </button>
      </div>
      <div className="term-list__item">
        <button
          type="button"
          className="term-list__drag-handle"
          aria-label={`Reorder ${task.title}`}
          tabIndex={-1}
        >
          <DragIco className="term-list__drag" />
        </button>
        <p>{task.title}</p>
        <button
          type="button"
          className="term-list__icon-btn"
          aria-label={`Edit ${task.title}`}
          onTouchStart={(e) => e.stopPropagation()}
          onClickCapture={(e) => {
            e.stopPropagation();
            onEditTask(termId, task);
          }}
        >
          <Pencil size={20} strokeWidth={1.8} />
        </button>
      </div>
      <label className="term-list__label desktop-show">
        <span>{task.career}</span>
        {taskCareerIsMissing && <CareerInfoTooltip isTouchDevice={isTouchDevice} />}
      </label>
      <div className={`term-list__select term-list__select--${getStatusClassName(task.status)}`}>
        <select
          value={task.status}
          aria-label={`Status for ${task.title}`}
          onChange={(event) => onUpdateTaskStatus(termId, task.id, event.target.value)}
        >
          <option value="not_started">Not Started</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>
        <ChevronDown size={24} strokeWidth={2.4} />
      </div>
      <button
        type="button"
        className="term-list__delete term-list__delete__desktop"
        aria-label={`Delete ${task.title}`}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={() => onOpenDeleteModal(termId, task)}
      >
        <X size={16} strokeWidth={1.8} />
      </button>
    </div>
  );
}

function ActionPlanTerm({
  term,
  isOpen,
  isDragOver,
  isDragging,
  isDropTarget,
  onAddTask,
  onDropIdea,
  onDragOver,
  onDragLeave,
  onToggle,
  onEditTask,
  onOpenDeleteModal,
  onUpdateTaskStatus,
  draggedTaskId,
  dragOverTaskId,
  onDesktopDragStart,
  onDesktopDragOver,
  onDesktopDrop,
  onDesktopDragEnd,
  onTouchStart,
  touchDraggedTaskId,
  ArrowPlayIco,
  exploredCareerNames,
}) {
  const tasks = term.tasks || [];

  const earnedPoints = tasks.reduce((total, task) => {
    switch (task.status) {
      case "completed":
        return total + 1;
      case "in_progress":
        return total + 0.5;
      default:
        return total;
    }
  }, 0);

  const termProgress = tasks.length
    ? Math.round((earnedPoints / tasks.length) * 100)
    : 0;
    
  return (
    <div
      data-action-plan-term-id={term.id}
      className={classNames("actionPlan__accordians--term", {
        "is-open": isOpen,
        "is-drag-over": isDragOver,
      })}
      onDragOver={(event) => onDragOver(event, term.id)}
      onDragLeave={(event) => onDragLeave(event, term.id)}
      onDrop={(event) => onDropIdea(event, term.id)}
    >
      <button
        type="button"
        className="actionPlan__accordians--term-header"
        aria-expanded={isOpen}
        aria-controls={`${term.id}-content`}
        onClick={() => onToggle(term.id)}
      >
        <div className="terms-header">
          {ArrowPlayIco && <img src={ArrowPlayIco} alt="arrow" className="actionPlan__accordians--icon" />}
          <h4>{term.title}</h4>
          <p>({term.duration})</p>
        </div>
        <div className="actionPlan__accordians--progress">
          <div className="actionPlan__accordians--progress-track" aria-hidden="true">
            <div className="actionPlan__accordians--progress-fill" style={{ width: `${termProgress}%` }} />
          </div>
        </div>
      </button>
      {isOpen && (
        <div className="actionPlan__accordians--term-content" id={`${term.id}-content`}>
          <p className={`actionPlan__accordians--subtitle ${term.tasks?.length ? '' : 'm-0'}`}>Things you can start right away</p>
          {(term.tasks || []).map((task, index) => (
            <TaskRow
              key={task.id}
              task={task}
              termId={term.id}
              index={index}
              isDragging={draggedTaskId === task.id}
              isDropTarget={dragOverTaskId === task.id}
              isTouchDragging={touchDraggedTaskId === task.id}
              onEditTask={onEditTask}
              onOpenDeleteModal={onOpenDeleteModal}
              onUpdateTaskStatus={onUpdateTaskStatus}
              onDesktopDragStart={onDesktopDragStart}
              onDesktopDragOver={onDesktopDragOver}
              onDesktopDrop={onDesktopDrop}
              onDesktopDragEnd={onDesktopDragEnd}
              onTouchStart={onTouchStart}
              exploredCareerNames={exploredCareerNames}
            />
          ))}
          <div className="actionPlan__accordians--addBtn">
            <button
              type="button"
              className="actionPlan__accordians--add"
              aria-label={`Add ${term.title} action item`}
              onClick={() => onAddTask(term.id)}
            >
              <Plus size={24} strokeWidth={1.8} />
            </button>
            <p>Add or drag a step to get started</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(ActionPlanTerm);
